import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Images, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, inputCls, btnPrimary } from "../../components/bits";

export default function AdminMarquee() {
  const [items, setItems] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/marquee").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post("/admin/marquee", fd);
      toast.success("تمت إضافة الصورة إلى الشريط المتحرك");
      setCaption("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/marquee/${id}`);
      toast.success("تم حذف الصورة من الشريط");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (items === null) return <div className="text-center text-mutext py-16">جارٍ التحميل …</div>;

  return (
    <div data-testid="admin-marquee-page">
      <PageHeader title="شريط الصور المتحرك" sub="إدارة صور اللوحة المتحركة في الصفحة الرئيسية — ما تضيفه هنا يظهر للزوار فوراً">
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="marquee-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="تعليق الصورة (اختياري)" className={inputCls} />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="marquee-file-input" />
          <button data-testid="marquee-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className={btnPrimary}>
            <ImagePlus className="w-4 h-4" /> {busy ? "جارٍ الرفع …" : "إضافة صورة"}
          </button>
        </div>
      </PageHeader>

      {items.length === 0 ? (
        <div data-testid="marquee-empty" className="text-center py-16 bg-surface rounded-3xl border border-brd">
          <Images className="w-10 h-10 mx-auto text-mutext mb-3" />
          <p className="text-sm text-mutext">لا توجد صور في الشريط بعد — أضف أول صورة</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`marquee-admin-item-${it.id.slice(0, 8)}`}
              className="relative rounded-2xl overflow-hidden border border-brd bg-surface shadow-sm group">
              <img src={it.url || `/api/public/marquee/file/${it.id}`} alt={it.caption || "صورة"} className="w-full h-36 object-cover" />
              <div className="px-3 py-2 text-[11px] font-bold truncate">{it.caption || "بدون تعليق"}</div>
              <button data-testid={`marquee-delete-${it.id.slice(0, 8)}`} onClick={() => remove(it.id)}
                title="حذف الصورة"
                className="absolute top-2 left-2 w-8 h-8 rounded-full bg-coral-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
